import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ToastService} from './toast.service';
import {throwError} from 'rxjs';
import {Router} from '@angular/router';
import {TranslateConfigService} from './translate-config.service';
import {UserService} from './user/user.service';
import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root'
})

export class HttpErrorHandlerService {
  loading = true

  constructor(private toastService: ToastService, private translateConfig: TranslateConfigService) {
    setTimeout(() => {
      this.loading = false;
    }, 3000); // Simulate loading for 3 seconds
  }

  // Mapa de mensajes conocidos del backend (en español) a claves i18n
  private backendMessageKeyMap: Record<string, string> = {
    'la contraseña actual es errónea': 'VALIDATORS.CURRENT_PASSWORD_WRONG',
    'la contraseña actual es incorrecta': 'VALIDATORS.CURRENT_PASSWORD_WRONG',
    'contraseña actual incorrecta': 'VALIDATORS.CURRENT_PASSWORD_WRONG',
    'contraseña actual errónea': 'VALIDATORS.CURRENT_PASSWORD_WRONG',
    'el correo electrónico ya está registrado en el sistema': 'AUTH.EMAIL_EXISTS',
    'el correo ya está registrado': 'AUTH.EMAIL_EXISTS',
    'el formato del correo electrónico no es válido': 'VALIDATORS.INVALID_EMAIL_FORMAT',
    'error al actualizar el perfil': 'ERRORS.UPDATE_PROFILE',
    // equivalentes de http genéricos por si llegan como texto
    'credenciales inválidas.': 'HTTP_ERRORS.401',
    'solicitud incorrecta.': 'HTTP_ERRORS.400',
    'no tiene permisos para acceder a este recurso.': 'HTTP_ERRORS.403',
    'recurso no encontrado.': 'HTTP_ERRORS.404',
    'error de validación.': 'HTTP_ERRORS.422',
    'error interno del servidor. inténtelo más tarde.': 'HTTP_ERRORS.500',
    'no se pudo conectar con el servidor.': 'HTTP_ERRORS.0',
    'ocurrió un error inesperado.': 'HTTP_ERRORS.UNEXPECTED'
  };

  private translateBackendMessage(message?: string): string | undefined {
    if (!message) return undefined;
    const normalized = message.trim().toLowerCase();
    const key = this.backendMessageKeyMap[normalized]
      || (normalized.includes('contraseña actual') && (normalized.includes('incorrecta') || normalized.includes('errónea'))
        ? 'VALIDATORS.CURRENT_PASSWORD_WRONG'
        : undefined)
      || (normalized.includes('correo') && normalized.includes('registr') ? 'AUTH.EMAIL_EXISTS' : undefined);
    if (key) {
      return this.translateConfig.getItemTranslated(key);
    }

    // Reglas dinámicas de longitud mínima y máxima
    const minLenMatchEs = normalized.match(/al\s+menos\s+(\d+)\s+caracteres/);
    const minLenMatchEn = normalized.match(/at\s+least\s+(\d+)\s+characters/);
    const minLen = minLenMatchEs?.[1] || minLenMatchEn?.[1];
    if (minLen) {
      return this.translateConfig.getItemTranslated('FORM_ERRORS.MIN_LENGTH', {min: Number(minLen)});
    }

    const maxLenMatchEs = normalized.match(/exceder\s+(los\s+)?(\d+)\s+caracteres/);
    const maxLenMatchEn = normalized.match(/exceed\s+(\d+)\s+characters/);
    const maxLen = maxLenMatchEs?.[2] || maxLenMatchEn?.[1];
    if (maxLen) {
      return this.translateConfig.getItemTranslated('FORM_ERRORS.MAX_LENGTH', {max: Number(maxLen)});
    }

    // Posibles reglas de complejidad (si el backend las usa literalmente)
    if (/(una|al\s+menos\s+una)\s+may(ú|u)scula|uppercase/.test(normalized)) {
      const t = this.translateConfig.getItemTranslated('VALIDATORS.PASSWORD_UPPERCASE');
      if (t) return t;
    }
    if (/(un|al\s+menos\s+un)\s+n(ú|u)mero|number/.test(normalized)) {
      const t = this.translateConfig.getItemTranslated('VALIDATORS.PASSWORD_NUMBER');
      if (t) return t;
    }
    if (/(un|al\s+menos\s+un)\s+car(á|a)cter\s+especial|special\s+character/.test(normalized)) {
      const t = this.translateConfig.getItemTranslated('VALIDATORS.PASSWORD_SPECIAL');
      if (t) return t;
    }

    return undefined;
  }

  handleError(error: HttpErrorResponse, toast: boolean = true) {
    const errorMessages: { [key: number]: string } = {
      401: this.translateConfig.getItemTranslated('HTTP_ERRORS.401'),
      400: this.translateConfig.getItemTranslated('HTTP_ERRORS.400'),
      403: this.translateConfig.getItemTranslated('HTTP_ERRORS.403'),
      404: this.translateConfig.getItemTranslated('HTTP_ERRORS.404'),
      422: this.translateConfig.getItemTranslated('HTTP_ERRORS.422'),
      500: this.translateConfig.getItemTranslated('HTTP_ERRORS.500'),
      0: this.translateConfig.getItemTranslated('HTTP_ERRORS.0'),
    };

    // Priorizar el mensaje del backend si existe, pero traducirlo si lo reconocemos
    const rawBackendMessage = (error as any)?.error?.message as string | undefined;
    let errorMessage = this.translateBackendMessage(rawBackendMessage)
      || rawBackendMessage
      || errorMessages[error.status]
      || this.translateConfig.getItemTranslated('HTTP_ERRORS.UNEXPECTED');

    // Construir errores de campos si hay 422
    let fieldErrors: Record<string, string[]> | undefined;

    if (error.status === 422 && error?.error?.errors) {
      const firstErrorKey = Object.keys(error.error.errors)[0];
      const firstMessage = error.error.errors[firstErrorKey]?.[0];
      errorMessage = this.translateBackendMessage(firstMessage) || firstMessage || errorMessage;
      // Traducir cada mensaje si es posible
      fieldErrors = Object.entries(error.error.errors).reduce((acc, [k, msgs]) => {
        const translatedMsgs = (msgs as string[]).map((m) => this.translateBackendMessage(m) || m);
        acc[k] = translatedMsgs;
        return acc;
      }, {} as Record<string, string[]>);
    }

    const normalizedError = {
      status: error.status,
      message: errorMessage,
      rawMessage: rawBackendMessage,
      fieldErrors
    } as {
      status: number;
      message: string;
      rawMessage?: string;
      fieldErrors?: Record<string, string[]>;
    };

    //if (toast) this.toastService.showError('Error', errorMessage);
    return throwError(() => normalizedError);
  }
}
